// Playbook Practice Mode — the agent rehearses the callback against an AI
// playing the customer (DISC-matched, grounded in the real deal context).
// Caps enforced server-side: 10 exchanges/session, 5 sessions/user/day.
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Send, Loader2, Sparkles, Flag } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PracticeModal = ({ callId, openingLine, onClose }) => {
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState(openingLine || '');
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [remaining, setRemaining] = useState(10);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [turns, feedback]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setSending(true);
    setTurns((t) => [...t, { role: 'agent', text: msg }]);
    setInput('');
    try {
      const r = await axios.post(`${API}/analytics/revival/${callId}/practice`, {
        action: 'reply', session_id: sessionId, message: msg,
      });
      setSessionId(r.data.session_id);
      setRemaining(r.data.turns_remaining);
      setTurns((t) => [...t, { role: 'customer', text: r.data.customer_reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Practice reply failed');
      setTurns((t) => t.slice(0, -1));
      setInput(msg);
    } finally {
      setSending(false);
    }
  };

  const getFeedback = async () => {
    if (!sessionId || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/analytics/revival/${callId}/practice`, {
        action: 'feedback', session_id: sessionId,
      });
      setFeedback(r.data.feedback);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not get feedback');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="practice-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="px-5 py-3.5 border-b flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-teal-600" /> Practice this callback</h3>
            <p className="text-[11px] text-slate-400">AI plays the customer. Start with your opening line — {remaining} exchanges left.</p>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-slate-500" data-testid="practice-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-[220px]">
          {turns.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-6">Say your opening line to start — the customer will pick up.</p>
          )}
          {turns.map((t, i) => (
            <div key={i} className={`flex ${t.role === 'agent' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-snug ${t.role === 'agent' ? 'bg-teal-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-800 rounded-bl-sm'}`}>
                {t.text}
              </div>
            </div>
          ))}
          {sending && <Loader2 className="w-4 h-4 animate-spin text-slate-300" />}
          {feedback && (
            <div className="border border-teal-200 bg-teal-50/60 rounded-xl p-4 space-y-2" data-testid="practice-feedback">
              <p className="text-sm font-bold text-teal-800">Coach feedback {feedback.score != null && <span className="ml-1">— {feedback.score}/10</span>}</p>
              {(feedback.went_well || []).map((w, i) => <p key={`w${i}`} className="text-xs text-emerald-700">✓ {w}</p>)}
              {(feedback.to_improve || []).map((w, i) => <p key={`i${i}`} className="text-xs text-amber-700">→ {w}</p>)}
              {feedback.one_liner && <p className="text-xs text-slate-600 italic pt-1">{feedback.one_liner}</p>}
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="px-5 py-3.5 border-t space-y-2">
          {!feedback && (
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder="What would you say?"
                disabled={sending || remaining <= 0}
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="practice-input"
              />
              <button
                onClick={send}
                disabled={sending || !input.trim() || remaining <= 0}
                className="bg-teal-600 hover:bg-teal-500 text-white rounded-lg px-3.5 disabled:opacity-40"
                data-testid="practice-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
          {sessionId && !feedback && (
            <button
              onClick={getFeedback}
              disabled={sending}
              className="w-full text-xs font-semibold text-teal-700 hover:text-teal-900 inline-flex items-center justify-center gap-1.5 py-1"
              data-testid="practice-end-feedback"
            >
              <Flag className="w-3.5 h-3.5" /> End session &amp; get coach feedback
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
